/**
 * File hashing utilities for document versioning.
 * Uses browser's SubtleCrypto API for SHA256 computation.
 */

/**
 * Maximum file size for hashing (500MB).
 * Files larger than this will skip hashing to prevent browser memory exhaustion.
 * Versioning will fall back to path-based detection for these files.
 */
export const MAX_HASH_FILE_SIZE = 500 * 1024 * 1024;

/**
 * Computes SHA256 hash of a file's content.
 * @param file - The file to hash
 * @returns Promise resolving to hex-encoded SHA256 hash (64 characters), or null if file is too large
 * @throws Error if hashing fails for reasons other than file size
 */
export async function computeFileHash(file: File): Promise<string | null> {
  // Skip hashing for very large files to prevent memory exhaustion
  if (file.size > MAX_HASH_FILE_SIZE) {
    console.warn(
      `[fileHash] File too large for hashing (${(file.size / (1024 * 1024)).toFixed(1)}MB > ${MAX_HASH_FILE_SIZE / (1024 * 1024)}MB). Skipping hash computation.`
    );
    return null;
  }

  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Computes SHA256 hash with progress callback for large files.
 * Uses chunked reading to avoid blocking the main thread.
 * @param file - The file to hash
 * @param onProgress - Optional callback with progress (0-1)
 * @returns Promise resolving to hex-encoded SHA256 hash, or null if file is too large
 */
export async function computeFileHashWithProgress(
  file: File,
  onProgress?: (progress: number) => void
): Promise<string | null> {
  // Skip hashing for very large files to prevent memory exhaustion
  if (file.size > MAX_HASH_FILE_SIZE) {
    console.warn(
      `[fileHash] File too large for hashing (${(file.size / (1024 * 1024)).toFixed(1)}MB > ${MAX_HASH_FILE_SIZE / (1024 * 1024)}MB). Skipping hash computation.`
    );
    onProgress?.(1);
    return null;
  }

  // For small files (<50MB), use simple approach
  if (file.size < 50 * 1024 * 1024) {
    onProgress?.(0.5);
    const hash = await computeFileHash(file);
    onProgress?.(1);
    return hash;
  }

  // For larger files, read in chunks to show progress
  // Note: SubtleCrypto doesn't support streaming, so we still need full buffer
  // but we can show progress during the file read phase
  const chunkSize = 10 * 1024 * 1024; // 10MB chunks
  const chunks: ArrayBuffer[] = [];
  let offset = 0;

  while (offset < file.size) {
    const chunk = file.slice(offset, offset + chunkSize);
    const buffer = await chunk.arrayBuffer();
    chunks.push(buffer);
    offset += chunkSize;
    onProgress?.((offset / file.size) * 0.8); // 80% for reading
  }

  // Combine chunks into single buffer
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
  const combined = new Uint8Array(totalLength);
  let position = 0;
  for (const chunk of chunks) {
    combined.set(new Uint8Array(chunk), position);
    position += chunk.byteLength;
  }

  onProgress?.(0.9); // 90% before hashing

  // Compute hash
  const hashBuffer = await crypto.subtle.digest("SHA-256", combined.buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hash = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");

  onProgress?.(1);
  return hash;
}

/**
 * Validates a hash string format.
 * @param hash - The hash string to validate
 * @returns true if valid SHA256 hex string (64 lowercase hex chars)
 */
export function isValidHash(hash: string): boolean {
  return /^[a-f0-9]{64}$/.test(hash);
}
